import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CardNumberElement, CardCvcElement, CardExpiryElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { connect } from 'react-redux';
import Summary from './Summary';
import InteractionOverlay from './InteractionOverlay';

import { asyncConfirmPaymentIntent, tokenizeStripe, tokenizeStripeSuccess, tokenizeStripeFailure } from 'actions/order';

const stripeElementStyle = {
  style: {
    base: {
      color: '#495057',
      fontWeight: 'normal',
      fontFamily: 'Montserrat, sans-serif',
      fontSize: '16px',
      lineHeight: 1.5,
      '::placeholder': { color: 'lightgray' }
    }
  }
};

const CardForm = ({
  user,
  bundleType,
  isProcessing,
  tokenize,
  tokenizeSuccess,
  tokenizeFailure,
  onTokenization,
  pendingStripeAction,
  confirmPayment
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [name, setName] = useState(user.name);

  useEffect(() => {
    // Stripe.js not loaded yet or no pendingStripe action
    if (!pendingStripeAction || !stripe) {
      return;
    }
    // show confirm payment popup (3DS)
    stripe.handleCardAction(pendingStripeAction.client_secret).then(() => {
      const secret = pendingStripeAction.client_secret;
      const paymentIntentId = secret.substring(0, secret.indexOf('_secret'));
      const invoiceId = pendingStripeAction.purchase_id;
      confirmPayment(paymentIntentId, invoiceId);
    });
  }, [pendingStripeAction, stripe, confirmPayment]);

  const tokenizeCard = async event => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);

    tokenize();

    stripe.createToken(cardElement).then(result => {
      if (result.error || !result.token) {
        tokenizeFailure({ error: result.error.message });
      } else {
        const token = result.token;
        tokenizeSuccess();
        onTokenization({
          ccNum: token.card.last4,
          ccExpireMonth: token.card.exp_month,
          ccExpireYear: token.card.exp_year,
          stripeToken: token.id
        });
      }
    });
  };

  return (
    <div className="payment-option credit-card p-4">
      <form className="form" onSubmit={event => tokenizeCard(event)}>
        <div className="form-group">
          <label htmlFor="ccName">Name on card</label>
          <input
            type="text"
            className="form-control"
            value={name}
            required
            onChange={event => setName(event.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="ccNum">Card number</label>
          <CardNumberElement className="form-control" options={stripeElementStyle} />
        </div>
        <div className="row">
          <div className="col-md-4 col-6">
            <div className="form-group">
              <label htmlFor="ccExpMonth">Expires on</label>
              <CardExpiryElement className="form-control" options={stripeElementStyle} />
            </div>
          </div>
          <div className="col-md-4 col-6">
            <div className="form-group">
              <label htmlFor="ccCVV">CVC code</label>
              <CardCvcElement className="form-control" options={stripeElementStyle} />
            </div>
          </div>
        </div>
        <input type="hidden" id="stripeToken" />
        <Summary bundleType={bundleType} className="mt-4" />
        <div className="d-flex justify-content-between mt-4">
          <div />
          <button className="btn btn-green" id="generateToken">
            {isProcessing ? 'Processing...' : 'Buy Now'}
          </button>
        </div>
      </form>
      <InteractionOverlay active={isProcessing} />
    </div>
  );
};

CardForm.propTypes = {
  bundleType: PropTypes.string.isRequired,
  onTokenization: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  isProcessing: state.order.isProcessing,
  pendingStripeAction: state.order.pendingStripeAction,
  user: state.user
});

const mapDispatchToProps = dispatch => ({
  tokenize: () => dispatch(tokenizeStripe()),
  tokenizeSuccess: () => dispatch(tokenizeStripeSuccess()),
  tokenizeFailure: payload => dispatch(tokenizeStripeFailure(payload)),
  confirmPayment: (paymentIntentId, invoiceId) => dispatch(asyncConfirmPaymentIntent(paymentIntentId, invoiceId))
});

export default connect(mapStateToProps, mapDispatchToProps)(CardForm);
